import React, { useEffect, useRef, useState } from "react";
import { MdCloudUpload, MdInsertDriveFile } from "react-icons/md";
import { truncateText } from "../Media/Media";
import { formatFileSize, showErrorToast } from "../../utils/Utils";
import { toast } from "react-toastify";
import ButtonLoading from "../Button loading/ButtonLoading";
import {
  useAddSocietyTimelineMutation,
  useUpdateSocietyTimelineMutation,
} from "../../redux/societyApi";

const AddUpdateTimelineEntry = ({
  id,
  oldData,
  open,
  setOpen,
  isNew,
  societyId,
  setSelectedTimeline,
}) => {
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    applicationNumber: "",
    applicationTitle: "",
    description: "",
  });

  const [docs, setDocs] = useState([]);
  const [deleteDocIds, setDeleteDocIds] = useState([]);
  const [errors, setErrors] = useState({});
  const [fileError, setFileError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const isEditMode = Boolean(id);
  useEffect(() => {
    if (!oldData) return;

    const entry = oldData;

    setFormData({
      applicationNumber: entry.applicationNo || "",
      applicationTitle: entry.title || "",
      description: entry.description || "",
    });

    if (entry.docs?.length > 0) {
      setDocs(
        entry.docs.map((att) => ({
          id: att._id || att.publicId,
          publicId: att.publicId,
          fileName: att.fileName || "Unknown",
          fileType: att.fileType || "Unknown",
          fileSize: att.fileSize || "Unknown",
          src: att.docUrl || null,
          isExisting: true,
          file: null,
        })),
      );
    }
  }, [isEditMode, oldData]);

  const [addSocietyTimeline, { isLoading: isCreateLoading }] =
    useAddSocietyTimelineMutation();
  const [updateSocietyTimeline, { isLoading: isUpdateLoading }] =
    useUpdateSocietyTimelineMutation();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFile = (file) => {
    setFileError("");

    if (!file) return;

    const maxFileSize = 10 * 1024 * 1024;
    if (file.size > maxFileSize) {
      setFileError("File size must be less than 10MB.");
      return;
    }

    const objectUrl = URL.createObjectURL(file);

    const newDoc = {
      id: "new_" + Date.now() + "_" + Math.random(),
      publicId: null,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      src: objectUrl,
      isExisting: false,
      file,
    };

    setDocs((prev) => [...prev, newDoc]);

    // reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    files.forEach((f) => handleFile(f));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleRemoveDoc = (doc) => {
    if (doc.isExisting && doc.publicId) {
      setDeleteDocIds((prev) => [...prev, doc.publicId]);
    }
    setDocs((prev) => prev.filter((d) => d.id !== doc.id));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    setFileError("");

    if (!formData.applicationNumber.trim()) {
      newErrors.applicationNumber = "Application number is required.";
      isValid = false;
    }

    if (!formData.applicationTitle.trim()) {
      newErrors.applicationTitle = "Title is required.";
      isValid = false;
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required.";
      isValid = false;
    }

    if (!isEditMode && docs.length === 0) {
      setFileError("Please upload at least one file.");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = new FormData();
    payload.append("applicationNo", formData.applicationNumber);
    payload.append("title", formData.applicationTitle);
    payload.append("description", formData.description);

    if (isEditMode && deleteDocIds.length > 0) {
      payload.append("deleteDocsIds", JSON.stringify(deleteDocIds));
    }

    docs
      .filter((d) => !d.isExisting && d.file)
      .forEach((d) => payload.append("docs", d.file));

    try {
      let response = null;

      if (!isNew && isEditMode) {
        response = await updateSocietyTimeline({
          timelineData: payload,
          id: id,
        }).unwrap();
      } else {
        response = await addSocietyTimeline({
          timelineData: payload,
          societyId: societyId,
        }).unwrap();
      }

      if (response.success) {
        handleCancel();
        toast.success(response.message);
      }
    } catch (err) {
      showErrorToast(err);
    }
  };

  const handleCancel = () => {
    setFormData({
      applicationNumber: "",
      applicationTitle: "",
      description: "",
    });
    setDocs([]);
    setDeleteDocIds([]);
    setErrors({});
    setFileError("");
    setSelectedTimeline(null);
    setOpen(false);

    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-[#FFFFFF05] backdrop-blur-sm backdrop-brightness-30 backdrop-opacity-60 z-[500]">
      <div className="w-[90%] mx-auto max-w-lg max-h-[90vh]  overflow-hidden border bg-white rounded-xl ">
        <div className="flex flex-col gap-3 max-h-[90vh] xl:p-8 lg:p-6 p-4 ">
          <div className=" shrink-0">
            <h1 className="text-[#05004E] lg:text-[1.5rem] md:text-[1.2rem] text-[1rem] font-semibold">
              {isEditMode ? "Update" : "Add New"} Timeline Entry
            </h1>
            <p className="text-[#5F6B7A] text-xs">
              Fill in the details to add a new application step to the society
              timeline.
            </p>
          </div>
          <div className=" grow overflow-y-auto">
            <form className="flex flex-col lg:gap-4 md:gap-3 gap-2 lg:text-[1rem] text-[.9rem]">
              {/* Application Number */}
              <div className="flex flex-col md:gap-1 gap-[2px]">
                <label className="font-semibold text-sm">
                  Application Number
                </label>
                <input
                  type="text"
                  name="applicationNumber"
                  placeholder="Enter Number"
                  value={formData.applicationNumber}
                  onChange={handleChange}
                  className="w-full border border-[#00000033] text-[#737791] outline-none p-3 rounded-xl"
                />
                {errors.applicationNumber && (
                  <p className="text-red-500 text-[12px]">
                    {errors.applicationNumber}
                  </p>
                )}
              </div>

              {/* Application Title */}
              <div className="flex flex-col md:gap-1 gap-[2px]">
                <label className="font-semibold text-sm">
                  Application Title
                </label>
                <input
                  type="text"
                  name="applicationTitle"
                  placeholder="Enter Title"
                  value={formData.applicationTitle}
                  onChange={handleChange}
                  className="w-full border border-[#00000033] text-[#737791] outline-none p-3 rounded-xl"
                />
                {errors.applicationTitle && (
                  <p className="text-red-500 text-[12px]">
                    {errors.applicationTitle}
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="flex flex-col md:gap-1 gap-[2px]">
                <label className="font-semibold text-sm">Description</label>
                <textarea
                  name="description"
                  placeholder="Enter the detail of application"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className="w-full border border-[#00000033] text-[#737791] outline-none p-3 rounded-xl resize-y"
                />
                {errors.description && (
                  <p className="text-red-500 text-[12px]">
                    {errors.description}
                  </p>
                )}
              </div>

              {/* Attachments */}
              <div className="flex flex-col md:gap-1 gap-[2px]">
                <div className="font-semibold text-sm">Attachments</div>

                {/* Drop zone */}
                <div
                  className={`w-full border border-dashed p-3 rounded-xl transition-colors ${
                    isDragging
                      ? "border-[#00643b] bg-[#02643B08]"
                      : "border-[#00000033]"
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <label
                    htmlFor="timeline-file-upload"
                    className="cursor-pointer flex flex-col md:gap-2 gap-1 md:py-4 py-2 items-center w-full"
                  >
                    <div className="md:p-3 p-1.5 bg-[#02643B20] rounded-full">
                      <MdCloudUpload className="lg:size-10 md:size-8 size-6 text-[#00643b]" />
                    </div>
                    <p className="text-center font-semibold text-[#0B0B0B]">
                      Drag your file(s) or{" "}
                      <span className="text-[#00643b]">browse</span>
                    </p>
                    <p className="text-[#6D6D6D] md:text-sm text-[12px]">
                      Max 10 MB files are allowed
                    </p>
                  </label>

                  <input
                    id="timeline-file-upload"
                    type="file"
                    multiple
                    accept="
                  image/*,
                  application/pdf,
                  application/msword,
                  application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                  application/vnd.ms-powerpoint,
                  application/vnd.openxmlformats-officedocument.presentationml.presentation,
                  application/vnd.ms-excel,
                  application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
                "
                    onChange={(e) =>
                      Array.from(e.target.files).forEach((f) => handleFile(f))
                    }
                    className="hidden"
                    ref={fileInputRef}
                  />
                </div>

                {fileError && (
                  <p className="text-red-500 text-[12px]">{fileError}</p>
                )}

                {/* File list */}
                {docs.length > 0 && (
                  <div className="flex flex-col gap-2 mt-2">
                    {docs.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between gap-2 lg:p-4 md:p-3 p-2 border border-[#00000033] rounded-lg"
                      >
                        <div className="flex items-center lg:gap-4 md:gap-3 gap-2 min-w-0">
                          {doc.fileType?.startsWith("image/") && doc.src ? (
                            <img
                              src={doc.src}
                              alt={doc.fileName}
                              className="lg:size-10 md:size-8 size-6 object-cover rounded-md flex-shrink-0"
                            />
                          ) : (
                            <MdInsertDriveFile className="lg:size-10 md:size-8 size-6 text-[#00643b] flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p
                              title={doc.fileName}
                              className="font-medium lg:text-sm md:text-[12px] text-[10px] capitalize truncate"
                            >
                              {truncateText(doc.fileName, 20)}
                            </p>
                            <p className="lg:text-sm md:text-[12px] text-[10px] text-[#6D6D6D]">
                              {formatFileSize(doc.fileSize)}
                            </p>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleRemoveDoc(doc)}
                          className="text-red-500 font-medium bg-[#66666620] md:p-2 py-1 px-2 md:text-[1rem] text-[.85rem] rounded-md cursor-pointer flex-shrink-0"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {isEditMode && deleteDocIds.length > 0 && (
                  <p className="text-[#6D6D6D] md:text-sm text-[12px]">
                    {deleteDocIds.length} existing attachment(s) will be removed
                    on update.
                  </p>
                )}
              </div>

              {/* Actions */}
            </form>
          </div>

          <div className="flex items-center shrink-0 justify-end lg:gap-4 md:gap-3 gap-2">
            <p
              onClick={handleCancel}
              disabled={isCreateLoading || isUpdateLoading}
              className="lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem] border border-[#00643B] text-[#00643B] text-center cursor-pointer lg:rounded-xl md:rounded-lg rounded-md font-medium"
            >
              Cancel
            </p>

            <button
              type="submit"
              disabled={isCreateLoading || isUpdateLoading}
              onClick={handleSubmit}
              className="lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem] border border-[#00643B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-white bg-[#00643B] lg:rounded-xl md:rounded-lg rounded-md font-medium"
            >
              {isEditMode ? (
                isUpdateLoading ? (
                  <ButtonLoading text="Update..." />
                ) : (
                  "Update"
                )
              ) : isCreateLoading ? (
                <ButtonLoading text="Adding..." />
              ) : (
                "Add Entry"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUpdateTimelineEntry;
